/**
 * White-label branding outside an agency (roadmap 12.3): the sign-in page and
 * public pages take the brand of the custom domain they are served on, or of
 * the public page's sub-account (GET /branding). Inside an agency, AgencyLayout
 * applies the agency's own branding instead.
 */
import { useEffect, useState } from 'react';
import type { Api } from '../api';
import type { PublicBranding } from '../api/billing';
import { useAuth } from '../auth/AuthProvider';
import { themeFromPublicBranding } from '../theme/theme';
import { useTheme } from '../theme/ThemeProvider';

/** Loads the host's branding once and makes it the base theme (rendered by Providers) */
export function HostBranding({ api }: { api: Api }) {
  const { setBaseTheme } = useTheme();
  useEffect(() => {
    let current = true;
    api.branding.get().then(
      (branding) => {
        if (current) setBaseTheme(themeFromPublicBranding(branding));
      },
      (error: unknown) => {
        // The default brand still works; say why the custom one didn't load
        console.warn('[branding] could not load the host branding; using the default', error);
      }
    );
    return () => {
      current = false;
    };
  }, [api, setBaseTheme]);
  return null;
}

/** A public page's brand (its sub-account's agency), applied while the page is shown */
export function usePageBranding(page: { pay?: string; booking?: string; portal?: string }): PublicBranding | null {
  const { api } = useAuth();
  const { setTheme, resetTheme } = useTheme();
  const [branding, setBranding] = useState<PublicBranding | null>(null);
  const { pay, booking, portal } = page;
  useEffect(() => {
    let current = true;
    api.branding.get({ ...(pay ? { pay } : {}), ...(booking ? { booking } : {}), ...(portal ? { portal } : {}) }).then(
      (result) => {
        if (!current) return;
        setBranding(result);
        if (result.source !== 'platform') setTheme(themeFromPublicBranding(result));
      },
      (error: unknown) => {
        console.warn('[branding] could not load this page’s branding; using the default', error);
      }
    );
    return () => {
      current = false;
      resetTheme();
    };
  }, [api, pay, booking, portal, setTheme, resetTheme]);
  return branding;
}

/** Logo and app name, for pages without the app header (sign-in, public pages) */
export function BrandMark() {
  const { theme } = useTheme();
  return (
    <p className="brand-mark">
      {theme.logoUrl ? <img src={theme.logoUrl} alt={`${theme.brandName} logo`} className="brand-logo" /> : null}
      <span className="brand-name">{theme.brandName}</span>
    </p>
  );
}

/** "Powered by <platform>", unless the agency turned it off */
export function PoweredBy() {
  const { theme } = useTheme();
  return theme.poweredBy ? <p className="powered-by muted">Powered by {theme.poweredBy}</p> : null;
}
